import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { z } from "zod";
import OnboardingDialog from "@/components/OnboardingDialog";


// Validation schemas
const emailSchema = z.string().trim().email("Invalid email address");

const passwordSchema = z.string()
  .min(8, "Password must be at least 8 characters")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[0-9]/, "Password must contain at least one number")
  .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character");

const nameSchema = z.string()
  .trim()
  .min(1, "Name is required")
  .max(100, "Name must be less than 100 characters")
  .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, "Name can only contain letters, spaces, hyphens, and apostrophes");

interface AuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AuthDialog = ({ open, onOpenChange }: AuthDialogProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [signupPassword, setSignupPassword] = useState("");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { toast } = useToast();
  const compassRef = useRef<HTMLDivElement>(null);
  const [needleAngle, setNeedleAngle] = useState(0);
  const [isInitialSpin, setIsInitialSpin] = useState(false);

  // Trigger initial spin when modal opens
  useEffect(() => {
    if (open) {
      setIsInitialSpin(true);
      const timer = setTimeout(() => setIsInitialSpin(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!compassRef.current) return;
    const rect = compassRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const angle = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI) + 90;
    setNeedleAngle(angle);
  }, []);

  const isPasswordValid = (password: string) => {
    return password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[^A-Za-z0-9]/.test(password);
  };

  const getPasswordHintColor = () => {
    if (signupPassword.length === 0) return "text-muted-foreground";
    if (isPasswordValid(signupPassword)) return "text-green-600";
    return "text-destructive";
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    const formData = new FormData(e.currentTarget);
    const email = formData.get("reset-email") as string;

    try {
      emailSchema.parse(email);
    } catch (error) {
      if (error instanceof z.ZodError) {
        setErrors({ 'reset-email': error.errors[0].message });
        setLoading(false);
        return;
      }
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    setLoading(false);

    if (error) {
      toast({
        title: t("auth.toastError"),
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: t("auth.toastEmailSent"),
        description: t("auth.toastEmailSentDesc"),
      });
      setShowResetPassword(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    const currentPath = window.location.pathname || '/';
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}${currentPath}`,
      },
    });

    if (error) {
      setLoading(false);
      toast({
        title: t("auth.toastGoogleError"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    
    const formData = new FormData(e.currentTarget);
    const email = formData.get("signup-email") as string;
    const password = formData.get("signup-password") as string;
    const fullName = formData.get("full-name") as string;

    try {
      emailSchema.parse(email);
      passwordSchema.parse(password);
      nameSchema.parse(fullName);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as string;
          newErrors[field] = err.message;
        });
        setErrors(newErrors);
        setLoading(false);
        toast({
          title: t("auth.toastValidationError"),
          description: t("auth.toastValidationErrorDesc"),
          variant: "destructive",
        });
        return;
      }
    }

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: {
          full_name: fullName,
        },
      },
    });

    setLoading(false);

    if (error) {
      toast({
        title: t("auth.toastSignupError"),
        description: error.message,
        variant: "destructive",
      });
    } else {
      try {
        await supabase.functions.invoke('send-welcome-email', {
          body: { email, name: fullName },
        });
      } catch (emailError) {
        console.error("Failed to send welcome email:", emailError);
      }

      toast({
        title: t("auth.toastAccountCreated"),
        description: t("auth.toastAccountCreatedDesc"),
      });
      onOpenChange(false);
      setShowOnboarding(true);
    }
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    
    const formData = new FormData(e.currentTarget);
    const email = formData.get("signin-email") as string;
    const password = formData.get("signin-password") as string;

    try {
      emailSchema.parse(email);
      z.string().min(1, "Password is required").parse(password);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as string;
          newErrors[field] = err.message;
        });
        setErrors(newErrors);
        setLoading(false);
        toast({
          title: t("auth.toastValidationError"),
          description: t("auth.toastValidationErrorDesc"),
          variant: "destructive",
        });
        return;
      }
    }

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    setLoading(false);

    if (error) {
      let errorMessage = error.message;
      
      if (error.message === "Invalid login credentials") {
        errorMessage = t("auth.invalidCredentials");
      } else if (error.message.includes("Email not confirmed")) {
        errorMessage = t("auth.emailNotConfirmed");
      } else if (error.message.includes("Too many requests")) {
        errorMessage = t("auth.tooManyAttempts");
      }
      
      toast({
        title: t("auth.toastLoginError"),
        description: errorMessage,
        variant: "destructive",
      });
    } else {
      toast({
        title: t("auth.toastLoginSuccess"),
        description: t("auth.toastLoginSuccessDesc"),
      });
      onOpenChange(false);
    }
  };

  const GoogleButton = () => (
    <Button
      variant="outline"
      className="w-full flex items-center justify-center gap-3 transition-none hover:bg-accent hover:text-accent-foreground hover:border-input"
      onClick={handleGoogleSignIn}
      disabled={loading}
    >
      <svg className="h-5 w-5" viewBox="0 0 24 24">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
      </svg>
      {loading ? t("auth.connecting") : t("auth.googleLogin")}
    </Button>
  );

  return (
    <>
    <Dialog open={open} onOpenChange={(isOpen) => {
      onOpenChange(isOpen);
      if (!isOpen) {
        setShowResetPassword(false);
        setSignupPassword("");
      }
    }}>
      <DialogContent className="sm:max-w-[800px] p-0 gap-0 overflow-hidden" onMouseMove={handleMouseMove}>
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Left: Compass Illustration */}
          <div
            ref={compassRef}
            className="hidden md:flex items-center justify-center bg-accent/30 p-8 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/20" />
            <svg viewBox="0 0 200 200" className="relative z-10 w-56 h-56 drop-shadow-lg">
              {/* Outer ring */}
              <circle cx="100" cy="100" r="95" fill="none" stroke="hsl(var(--border))" strokeWidth="2" />
              <circle cx="100" cy="100" r="88" fill="hsl(var(--card))" stroke="hsl(var(--border))" strokeWidth="1" />
              {/* Cardinal marks */}
              {["N", "E", "S", "W"].map((label, i) => {
                const angle = i * 90;
                const rad = (angle - 90) * (Math.PI / 180);
                const tx = 100 + Math.cos(rad) * 74;
                const ty = 100 + Math.sin(rad) * 74;
                return (
                  <text
                    key={label}
                    x={tx}
                    y={ty}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className="fill-muted-foreground text-xs font-bold"
                    style={{ fontSize: 14 }}
                  >
                    {label}
                  </text>
                );
              })}
              {/* Tick marks */}
              {Array.from({ length: 36 }).map((_, i) => {
                const angle = i * 10;
                const rad = (angle - 90) * (Math.PI / 180);
                const major = angle % 90 === 0;
                const r1 = major ? 84 : 86;
                const r2 = 90;
                return (
                  <line
                    key={i}
                    x1={100 + Math.cos(rad) * r1}
                    y1={100 + Math.sin(rad) * r1}
                    x2={100 + Math.cos(rad) * r2}
                    y2={100 + Math.sin(rad) * r2}
                    stroke="hsl(var(--muted-foreground))"
                    strokeWidth={major ? 2 : 1}
                    opacity={major ? 1 : 0.4}
                  />
                );
              })}
              {/* Inner circle */}
              <circle cx="100" cy="100" r="60" fill="none" stroke="hsl(var(--border))" strokeWidth="0.5" opacity="0.3" />
              {/* Needle group — rotates to follow mouse */}
              <g
                style={{
                  transform: `rotate(${needleAngle}deg)`,
                  transformOrigin: "100px 100px",
                  transition: isInitialSpin ? "none" : "transform 0.15s ease-out",
                  animation: isInitialSpin ? "spin 1s ease-out forwards" : "none",
                }}
              >
                {/* North (red) needle */}
                <polygon points="100,30 93,100 107,100" fill="hsl(var(--primary))" opacity="0.9" />
                {/* South (dark) needle */}
                <polygon points="100,170 93,100 107,100" fill="hsl(var(--muted-foreground))" opacity="0.4" />
              </g>
              {/* Center pin */}
              <circle cx="100" cy="100" r="5" fill="hsl(var(--primary))" />
              <circle cx="100" cy="100" r="2.5" fill="hsl(var(--primary-foreground))" />
            </svg>
          </div>

          {/* Right: Form */}
          <div className="p-6">
            {showResetPassword ? (
              <>
                <DialogHeader>
                  <DialogTitle>{t("auth.resetPassword")}</DialogTitle>
                  <DialogDescription>
                    {t("auth.resetPasswordDescription")}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleResetPassword} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="reset-email">{t("auth.email")}</Label>
                    <Input
                      id="reset-email"
                      name="reset-email"
                      type="email"
                      placeholder={t("auth.emailPlaceholder")}
                      required
                      className={errors['reset-email'] ? 'border-destructive' : ''}
                    />
                    {errors['reset-email'] && (
                      <p className="text-sm text-destructive">{errors['reset-email']}</p>
                    )}
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? t("auth.sending") : t("auth.sendResetLink")}
                  </Button>
                  <button
                    type="button"
                    className="w-full text-sm text-muted-foreground hover:underline"
                    onClick={() => setShowResetPassword(false)}
                  >
                    {t("auth.backToLogin")}
                  </button>
                </form>
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>{t("auth.loginSignup")}</DialogTitle>
                </DialogHeader>

                <Tabs defaultValue="signin" className="w-full mt-4">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="signin">{t("auth.login")}</TabsTrigger>
                    <TabsTrigger value="signup">{t("auth.signup")}</TabsTrigger>
                  </TabsList>

                  <TabsContent value="signin">
                    <form onSubmit={handleSignIn} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="signin-email">{t("auth.email")}</Label>
                        <Input
                          id="signin-email"
                          name="signin-email"
                          type="email"
                          placeholder={t("auth.emailPlaceholder")}
                          required
                          className={errors['signin-email'] ? 'border-destructive' : ''}
                        />
                        {errors['signin-email'] && (
                          <p className="text-sm text-destructive">{errors['signin-email']}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signin-password">{t("auth.password")}</Label>
                        <Input
                          id="signin-password"
                          name="signin-password"
                          type="password"
                          placeholder={t("auth.passwordPlaceholder")}
                          required
                          className={errors['signin-password'] ? 'border-destructive' : ''}
                        />
                        {errors['signin-password'] && (
                          <p className="text-sm text-destructive">{errors['signin-password']}</p>
                        )}
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          className="text-sm text-primary hover:underline"
                          onClick={() => setShowResetPassword(true)}
                        >
                          {t("auth.forgotPassword")}
                        </button>
                      </div>
                      <Button type="submit" className="w-full" disabled={loading}>
                        {loading ? t("auth.connecting") : t("auth.loginCta")}
                      </Button>

                      {/* Google under login button */}
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground">{t("auth.orContinueWith")}</span>
                        </div>
                      </div>
                      <GoogleButton />
                    </form>
                  </TabsContent>

                  <TabsContent value="signup">
                    <form onSubmit={handleSignUp} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="full-name">{t("auth.fullName")}</Label>
                        <Input
                          id="full-name"
                          name="full-name"
                          type="text"
                          placeholder={t("auth.fullNamePlaceholder")}
                          required
                          className={errors['full-name'] ? 'border-destructive' : ''}
                        />
                        {errors['full-name'] && (
                          <p className="text-sm text-destructive">{errors['full-name']}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-email">{t("auth.email")}</Label>
                        <Input
                          id="signup-email"
                          name="signup-email"
                          type="email"
                          placeholder={t("auth.emailPlaceholder")}
                          required
                          className={errors['signup-email'] ? 'border-destructive' : ''}
                        />
                        {errors['signup-email'] && (
                          <p className="text-sm text-destructive">{errors['signup-email']}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-password">{t("auth.password")}</Label>
                        <Input
                          id="signup-password"
                          name="signup-password"
                          type="password"
                          placeholder={t("auth.passwordPlaceholder")}
                          required
                          value={signupPassword}
                          onChange={(e) => setSignupPassword(e.target.value)}
                          className={errors['signup-password'] ? 'border-destructive' : ''}
                        />
                        {errors['signup-password'] && (
                          <p className="text-sm text-destructive">{errors['signup-password']}</p>
                        )}
                        <p className={`text-xs mt-1 ${getPasswordHintColor()}`}>
                          {t("auth.passwordHint")}
                        </p>
                      </div>
                      <Button type="submit" className="w-full" disabled={loading}>
                        {loading ? t("auth.creating") : t("auth.createAccount")}
                      </Button>

                      {/* Google under signup button */}
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground">{t("auth.orContinueWith")}</span>
                        </div>
                      </div>
                      <GoogleButton />
                    </form>
                  </TabsContent>
                </Tabs>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
    
    <OnboardingDialog 
      open={showOnboarding} 
      onOpenChange={setShowOnboarding} 
    />
    </>
  );
};

export default AuthDialog;
